import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { useCallback, useState, useMemo } from "react";
import { CallApiGetVerifyCode } from "../Api/Enforcement";
import { accuracy, rePass } from "../Api/SubUrl";
import { SwalSuccess } from "../Share/Swal";
import useCookieStorage from "./useCookieStorage";
import {
  FORGET_PASS_USER_ID_KEY,
  FORGET_PASS_PHONE_KEY,
} from "../Constants/localStorageKey";
import { ShowCustomErrorModal } from "../Components/Atoms/CustomErrorModal";
import useReCaptcha from "./useReCaptcha";

const normalizePhoneForApi = (phone = "") => {
  const trimmed = phone.trim();
  if (trimmed.startsWith("+84")) {
    return `0${trimmed.slice(3)}`;
  }
  return trimmed;
};

const mapVerifyPayload = (formData) => ({
  AccountName: formData.accountName?.trim().toLowerCase() ?? "",
  PhoneNumber: normalizePhoneForApi(formData.phoneNumber),
  Type: "FORGET_PASS"
});

export default function useClientAccuracy() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setValue } = useCookieStorage();
  const {
    version,
    reCaptchaV2,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    getReCaptchaToken,
    resetReCaptcha,
    isDefaultV3,
  } = useReCaptcha("getVerifyCode", accuracy);
  const verifySchema = yup.object().shape({
    accountName: yup
      .string()
      .required("Vui lòng nhập tên tài khoản")
      .min(5, "Tên tài khoản phải có độ dài từ 5 đến 10 ký tự")
      .max(10, "Tên tài khoản phải có độ dài từ 5 đến 10 ký tự")
      .matches(
        "^[a-z0-9]*$",
        "Tên tài khoản chỉ được sử dụng các ký tự: a-z, 0-9"
      ),
    phoneNumber: yup
      .string()
      .trim()
      .required("Vui lòng nhập số điện thoại")
      .test(
        "phone-number",
        "Số điện thoại không hợp lệ.",
        (value) => /^0(3|5|7|8|9)[0-9]{8}$/.test(normalizePhoneForApi(value))
      ),
  });

  const { control, handleSubmit, reset } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: useMemo(
      () => ({
        accountName: "",
        phoneNumber: "",
      }),
      []
    ),
    resolver: yupResolver(verifySchema),
  });

  const handleVerify = useCallback(
    async (formData) => {
      if (loading) return;
      setLoading(true);
      try {
        const token = await getReCaptchaToken();
        const payload = mapVerifyPayload(formData);

        CallApiGetVerifyCode(token, payload, (res) => {
          setLoading(false);
          if (res?.Status) {
            resetReCaptcha();
            const response = res.Status;
            const errorMessage =
              typeof response.data === "string"
                ? response.data
                : response.data?.Message ??
                  "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ";

            ShowCustomErrorModal({
              message: `<p>${errorMessage}</p>`,
              title: response.status === 400 ? "Cập nhật thất bại" : "Đã xảy ra lỗi",
            });
            return;
          }

          if (!res?.success) {
            resetReCaptcha();
            ShowCustomErrorModal({
              message:
                "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
              title: "Đã xảy ra lỗi",
            });
            return;
          }

          reset();
          setValue(FORGET_PASS_USER_ID_KEY, payload.AccountName);
          setValue(FORGET_PASS_PHONE_KEY, payload.PhoneNumber);
          SwalSuccess("Mã xác nhận đã được gửi tới số điện thoại của bạn!");
          navigate(rePass);
        });
      } catch (error) {
        setLoading(false);
        resetReCaptcha();
        ShowCustomErrorModal({
          message: error.message,
          title: "Xác thực thất bại",
        });
      }
    },
    [getReCaptchaToken, loading, navigate, reset, resetReCaptcha, setValue]
  );

  const handleInvalid = useCallback((errors) => {
    const errorMessages = Object.values(errors)
      .map((err) => `<p>${err.message}</p>`)
      .join("");
    ShowCustomErrorModal({
      message: errorMessages,
      title: "Đăng nhập thất bại",
    });
  }, []);
  return {
    control,
    handleSubmit: handleSubmit(handleVerify, handleInvalid),
    loading,
    version,
    reCaptchaV2,
    isLoadReCaptChaV3,
    recaptchaLoading,
    recaptchaError,
    isDefaultV3,
  };
}
